import React, { useState } from 'react';
import api from '../services/api';
import AnnouncementBar from '../components/AnnouncementBar';
import Footer from '../components/Footer';
import SponsorsSection from '../components/SponsorsSection';
import ScrollToTop from '../components/ScrollToTop';
import TornPaperWrapper from '../components/TornPaperWrapper';
import { trackFormSubmit } from '../utils/ga4';
import './BazaarVendor.css';
import './Partners.css';
import { getCmsImagePath } from '../cms/cmsData';
import { getArabicTranslation } from '../utils/arabicTranslations';

const AFFILIATE_TERMS = `1. Eligibility & Approval
Participation is by approval only. OC MENA Festival may accept or decline any application at its sole discretion.

2. Promo Codes & Attribution
Affiliates receive a unique promo code ("Code"). Commission is earned only on orders where the Code is successfully applied at checkout. Codes may not be combined with other promotions unless explicitly authorized.

3. Commission & Payment
Commission rates are assigned by OC MENA Festival and may vary by affiliate. Commissions are calculated on qualifying ticket revenue (excluding taxes, fees, chargebacks, and refunded orders). Payments are issued after the event or on a schedule determined by OC MENA Festival, subject to a minimum payout threshold (e.g., $100).

4. Refunds, Chargebacks & Cancellations
Orders that are refunded, canceled, disputed, or charged back are not commissionable. Any commissions previously credited for such orders may be reversed.

5. Prohibited Conduct
Affiliates may not:

Use misleading claims, false scarcity, or impersonation

Buy ads bidding on OC MENA trademarks or brand terms without written approval

Use spam, purchased lists, or automated messaging in violation of platform policies

Use their own Code for personal purchases (optional rule, but recommended)

Offer unauthorized discounts or promises

6. Brand Guidelines
Affiliates agree to represent OC MENA Festival in a professional manner consistent with festival branding and community values.

7. Termination
OC MENA Festival may suspend or terminate participation at any time, with or without notice, including for suspected fraud or policy violations. Upon termination, unused Codes may be disabled and unpaid commissions may be withheld in cases of fraud or abuse.

8. Changes to Program
OC MENA Festival may modify commission rates, policies, or program structure at any time.`;

const Partners = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    partnerType: 'Influencer',
    instagram: '',
    tiktok: '',
    otherSocial: '',
    followerCount: '',
    primaryAudience: '',
    involvement: '',
    acceptTerms: false
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [termsError, setTermsError] = useState(false);

  const partnerTypes = [
    'Influencer',
    'Brand/Company',
    'Media',
    'Photographer',
    'Videographer',
    'Decorator',
    'Artist',
    'DJ'
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setError('');
    if (name === 'acceptTerms') setTermsError(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const missing = [];
    if (!formData.firstName) missing.push('First Name');
    if (!formData.email) missing.push('Email');
    if (!formData.partnerType) missing.push('Partner Type');
    if (!formData.involvement) missing.push('How would you like to be involved');
    if (missing.length > 0) {
      setError(`Please fill in the following: ${missing.join(', ')}`);
      return;
    }

    if (!formData.acceptTerms) {
      setTermsError(true);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const submitData = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        partner_type: formData.partnerType,
        instagram: formData.instagram,
        tiktok: formData.tiktok,
        other_social: formData.otherSocial,
        follower_count: formData.followerCount,
        primary_audience: formData.primaryAudience,
        involvement: formData.involvement,
        accept_terms: formData.acceptTerms
      };

      const result = await api.submitPartnerApplication(submitData);

      if (result?.success) {
        setSuccess(true);
        setError('');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        trackFormSubmit('partners_form_submit', {
          formName: 'partners_page_form',
          formLocation: 'partners_page',
        });

        setTimeout(() => {
          setFormData({
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            partnerType: 'Influencer',
            instagram: '',
            tiktok: '',
            otherSocial: '',
            followerCount: '',
            primaryAudience: '',
            involvement: '',
            acceptTerms: false
          });
          setSuccess(false);
        }, 5000);
      } else {
        setError(result?.error?.message || 'Failed to submit application');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (err) {
      console.error('Partner application error:', err);
      // Handle JSON parse errors (backend not running or returning HTML)
      if (err.message?.includes('Unexpected token') || err.message?.includes('not valid JSON')) {
        setError('Unable to connect to server. Please try again later or contact us directly at admin@ocmenafestival.com');
      } else {
        setError(err.message || 'Failed to submit application. Please try again.');
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-wrapper">
      <AnnouncementBar />

      <section className="hero-section">
        <div className="hero-background-wrapper">
          <video src={"/hero-final.mp4"} alt="oc mena festival" className="hero-background-video" autoPlay muted loop playsInline />
          <div className="hero-gradient-overlay"></div>
        </div>

        <TornPaperWrapper>
          <h1 className="card-title">Partners <span className="arabic-text on-white">{getArabicTranslation('Partners')}</span></h1>
          <p className="card-subtitle" style={{ fontWeight: '700' }}>
            Love festivals? Love culture? Want to be part of something big?
          </p>
          <p className="card-subtitle">
            We're recruiting brand ambassadors to help spread the word in the form of influencers, photographers, videographers, and more.
          </p>
          <p className="card-subtitle">
            We're also open to partnerships with brands and companies across the Southern California Region. Fill out the form below or contact us
            by phone or email. If you want to inquire about sponsorship opportunities, <a href="/sponsors" style={{ color: '#dc2626' }}>click here</a>.
          </p>

          {error && <div className="error-message" role="alert">{error}</div>}
          {success && <div className="success-message" role="status">Thank you! Your application has been submitted successfully. We'll be in touch soon.</div>}

          <form onSubmit={handleSubmit} className="vendor-form">
            <div className="form-group">
              <label htmlFor="firstName">First name <span style={{ color: '#dc2626' }}>*</span></label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="lastName">Last name</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Email <span style={{ color: '#dc2626' }}>*</span></label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="phone">Phone</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="partnerType">Partner Type <span style={{ color: '#dc2626' }}>*</span></label>
              <select
                id="partnerType"
                name="partnerType"
                value={formData.partnerType}
                onChange={handleChange}
                required
              >
                {partnerTypes.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="instagram">Instagram</label>
              <input
                type="text"
                id="instagram"
                name="instagram"
                value={formData.instagram}
                onChange={handleChange}
                placeholder="@youraccount"
              />
            </div>

            <div className="form-group">
              <label htmlFor="tiktok">TikTok</label>
              <input
                type="text"
                id="tiktok"
                name="tiktok"
                value={formData.tiktok}
                onChange={handleChange}
                placeholder="@youraccount"
              />
            </div>

            <div className="form-group">
              <label htmlFor="otherSocial">Other Social Media Account</label>
              <input
                type="text"
                id="otherSocial"
                name="otherSocial"
                value={formData.otherSocial}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="followerCount">Follower Count (primary platform)</label>
              <input
                type="text"
                id="followerCount"
                name="followerCount"
                value={formData.followerCount}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="primaryAudience">Primary Audience (city/state)</label>
              <input
                type="text"
                id="primaryAudience"
                name="primaryAudience"
                value={formData.primaryAudience}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="involvement">How would you like to be involved with the OC MENA Festival? <span style={{ color: '#dc2626' }}>*</span></label>
              <textarea
                id="involvement"
                name="involvement"
                value={formData.involvement}
                onChange={handleChange}
                rows={4}
                required
              />
            </div>

            <label className="form-label" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.95rem' }}>Terms & Conditions <span style={{ color: '#dc2626' }}>*</span></label>
            <div className="partner-terms-box">
              <div className="partner-terms-scroll">
                {AFFILIATE_TERMS}
              </div>
            </div>

            <div className="form-group" style={termsError ? { padding: '12px', border: '2px solid #dc2626', borderRadius: '8px', backgroundColor: '#fef2f2' } : {}}>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  id="acceptTerms"
                  name="acceptTerms"
                  checked={formData.acceptTerms}
                  onChange={handleChange}
                  className="checkbox-input"
                />
                <span className="checkbox-custom"></span>
                <span>I accept the terms & conditions above</span>
              </label>
              {termsError && <p style={{ color: '#dc2626', fontSize: '13px', margin: '8px 0 0', fontWeight: '600' }}>Please accept the terms & conditions to continue</p>}
            </div>

            <button type="submit" className="continue-btn" disabled={loading}>
              {loading ? 'Submitting...' : 'Submit'}
            </button>
          </form>
        </TornPaperWrapper>

        <div className="lanterns-container">
          <img loading="lazy" src={getCmsImagePath("lanterns", "/lanterns.webp")} alt="festival lanterns" className="lanterns-image" />
        </div>
      </section>

      <SponsorsSection />
      <Footer />
      <ScrollToTop />
    </div>
  );
};

export default Partners;
